import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { openErrorNotification } from "helpers/methods";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { getRelativeFontSize, mediumFont, regularFont } from "utils/styles";
import { CustomContactNumberInput } from "global/components/CustomContactNumberInput/CustomContactNumberInput";
import { setAuthUserIdInStorage } from "screens/Blogs/blogsUserPreferences";
import { loginAction } from "../../redux/authSlice";
import { signUpUser } from "./AuthServices";
import ValidateOTP from "./ValidateOTP";

const BRAND = "#f8481c";
const BRAND_LIGHT = "#ff6b4a";

const textFieldSx = (theme: Theme) => {
  const isDark = theme.palette.mode === "dark";
  return {
    mb: 2,
    "& .MuiOutlinedInput-root": {
      ...regularFont,
      fontSize: getRelativeFontSize(0),
      backgroundColor: isDark
        ? alpha(theme.palette.background.default, 0.5)
        : alpha(theme.palette.background.paper, 0.9),
      borderRadius: "12px",
      "& fieldset": {
        borderWidth: 1.5,
        borderColor: isDark
          ? alpha(theme.palette.divider, 0.5)
          : alpha(theme.palette.common.black, 0.22),
      },
      "&:hover fieldset": {
        borderColor: alpha(BRAND, 0.6),
      },
      "&.Mui-focused fieldset": {
        borderColor: BRAND,
        borderWidth: 2,
        boxShadow: `0 0 0 4px ${alpha(BRAND, 0.15)}`,
      },
    },
    "& .MuiInputBase-input": {
      color: theme.palette.text.primary,
      "&::placeholder": {
        fontSize: getRelativeFontSize(0),
        opacity: 0.8,
      },
    },
  };
};

const SignUp: React.FC<{ onBack?: () => void }> = ({ onBack }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const history = useHistory();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState("");

  const [otpOpen, setOtpOpen] = useState(false);
  const [otpToken, setOtpToken] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validate = () => {
    const errs: any = {};
    if (!name) errs.name = "Name is required";
    if (!email) errs.email = "Email is required";
    else if (!/^\S+@\S+\.\S+$/.test(email)) errs.email = "Invalid email";
    if (!password) errs.password = "Password is required";
    if (!confirmPassword) errs.confirmPassword = "Confirm your password";
    else if (password !== confirmPassword)
      errs.confirmPassword = "Passwords do not match";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSignUp = async () => {
    if (!validate()) return;
    setServerError("");
    setLoading(true);
    try {
      const resp: any = await signUpUser(
        name,
        email,
        password,
        contactNumber.trim() || undefined
      );
      const token =
        resp?.authToken ||
        resp?.token ||
        resp?.auth_token ||
        resp?.access_token;
      if (token) {
        setOtpToken(token);
        setOtpOpen(true);
        return;
      }
      if (resp?.access_token || resp?.authToken) {
        const userId = resp?.user?.id || "";
        dispatch(
          loginAction({
            authenticated: true,
            accessToken: resp?.access_token || resp?.authToken,
            refreshToken: resp?.refresh_token || "",
            userName: resp?.user?.name || name,
            email: resp?.user?.email || email,
            userRole: resp?.user?.role || "",
            companyId: resp?.companyId || "",
            resources: resp?.resources || [],
          }),
        );
        setAuthUserIdInStorage(userId);
        history.push("/");
        return;
      }
      setServerError(
        "Registration successful. Please check your email for OTP.",
      );
      setOtpOpen(true);
    } catch (error: any) {
      const msg =
        error?.message || error?.errorMessage || "Registration failed";
      setServerError(msg);
      openErrorNotification(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpVerified = (resp: any) => {
    const accessToken =
      resp?.authToken || resp?.access_token || resp?.token || resp?.auth_token;
    const refreshToken = resp?.refreshToken || resp?.refresh_token || "";

    if (accessToken) {
      const userId = resp?.user?.id || "";
      dispatch(
        loginAction({
          authenticated: true,
          accessToken: accessToken,
          refreshToken: refreshToken,
          userName: resp?.user?.name || name,
          email: resp?.user?.email || email,
          userRole: resp?.user?.role || "",
          companyId: resp?.companyId || "",
          resources: resp?.resources || [],
        }),
      );
      setAuthUserIdInStorage(userId);
      history.push("/");
    }
  };

  return (
    <Box sx={{ mt: 0 }}>
      <TextField
        label="Name"
        variant="outlined"
        value={name}
        onChange={(e) => setName(e.target.value)}
        error={!!errors.name}
        helperText={errors.name}
        fullWidth
        sx={textFieldSx(theme)}
        InputLabelProps={{
          shrink: !!name,
          sx: {
            ...regularFont,
            fontSize: getRelativeFontSize(0),
            color: theme.palette.text.secondary,
          },
        }}
      />

      <TextField
        label="Email"
        variant="outlined"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={!!errors.email}
        helperText={errors.email}
        fullWidth
        sx={textFieldSx(theme)}
        InputLabelProps={{
          shrink: !!email,
          sx: {
            ...regularFont,
            fontSize: getRelativeFontSize(0),
            color: theme.palette.text.secondary,
          },
        }}
      />

      <Box sx={{ mb: 2 }}>
        <CustomContactNumberInput
          label="Contact number (optional)"
          value={contactNumber}
          onChange={setContactNumber}
          placeHolder="Phone number"
          sx={{
            ...textFieldSx(theme),
            mb: 0,
          }}
          InputLabelProps={{
            shrink: !!contactNumber,
            sx: {
              ...regularFont,
              fontSize: getRelativeFontSize(0),
              color: theme.palette.text.secondary,
            },
          }}
        />
      </Box>

      <TextField
        label="Password"
        variant="outlined"
        type={showPassword ? "text" : "password"}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        error={!!errors.password}
        helperText={errors.password}
        fullWidth
        sx={{ ...textFieldSx(theme) }}
        InputLabelProps={{
          shrink: !!password,
          sx: {
            ...regularFont,
            fontSize: getRelativeFontSize(0),
            color: theme.palette.text.secondary,
          },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={() => setShowPassword((s) => !s)}
                edge="end"
                sx={{
                  color: theme.palette.text.secondary,
                  "&:hover": { color: BRAND },
                }}
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <TextField
        label="Confirm Password"
        variant="outlined"
        type={showConfirmPassword ? "text" : "password"}
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        error={!!errors.confirmPassword}
        helperText={errors.confirmPassword}
        fullWidth
        sx={textFieldSx(theme)}
        InputLabelProps={{
          shrink: !!confirmPassword,
          sx: {
            ...regularFont,
            fontSize: getRelativeFontSize(0),
            color: theme.palette.text.secondary,
          },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle confirm password visibility"
                onClick={() => setShowConfirmPassword((s) => !s)}
                edge="end"
                sx={{
                  color: theme.palette.text.secondary,
                  "&:hover": { color: BRAND },
                }}
              >
                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {serverError && (
        <Typography
          variant="body2"
          color="error"
          sx={{
            mb: 1,
            ...regularFont,
            fontSize: getRelativeFontSize(0),
          }}
        >
          {serverError}
        </Typography>
      )}

      <Box sx={{ mt: 1 }}>
        <Button
          variant="contained"
          fullWidth
          sx={{
            ...mediumFont,
            minHeight: 48,
            py: 1.5,
            fontSize: getRelativeFontSize(1),
            textTransform: "none",
            borderRadius: "12px",
            background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_LIGHT} 100%)`,
            boxShadow: `0 4px 20px ${alpha(BRAND, 0.35)}`,
            "&:hover": {
              background: `linear-gradient(135deg, ${BRAND_LIGHT} 0%, ${BRAND} 100%)`,
              boxShadow: `0 6px 24px ${alpha(BRAND, 0.45)}`,
            },
          }}
          onClick={handleSignUp}
          disabled={loading}
        >
          {loading ? <CircularProgress size={22} color="inherit" /> : "Sign up"}
        </Button>
      </Box>

      <ValidateOTP
        open={otpOpen}
        token={otpToken}
        email={email}
        onClose={() => setOtpOpen(false)}
        onVerified={handleOtpVerified}
      />
    </Box>
  );
};

export default SignUp;
